if (!customElements.get('testimonial-carousel')) {
  class TestimonialCarousel extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('slider-component');
      if (!this.slider) return;

      this.sliderList = this.slider.querySelector('[id^="Slider-"]');
      this.videos = this.querySelectorAll('video');
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.autoplay = null;
      this.isPaused = false;

      // Reduced motion check
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);
      this.reducedMotion.addEventListener('change', this.handleReducedMotionChange);

      // Bind handlers for cleanup
      this.handleMouseEnter = () => this.pause();
      this.handleMouseLeave = () => {
        if (!this.isPaused) this.play();
      };
      this.handleFocusIn = () => this.pause();
      this.handleFocusOut = (e) => {
        if (!this.contains(e.relatedTarget) && !this.isPaused) this.play();
      };
      this.handleSlideChanged = this.onSlideChanged.bind(this);
      this.handleBlockSelect = this.onBlockSelect.bind(this);
      this.handleBlockDeselect = this.onBlockDeselect.bind(this);

      // Pause on hover/focus
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);

      // Slide change (video pause + restart auto-rotation timer)
      this.slider.addEventListener('slideChanged', this.handleSlideChanged);

      // Theme editor support
      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', this.handleBlockSelect);
        this.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }

      // Entrance animation via IntersectionObserver
      this.section = this.closest('.product-testimonials');
      if (this.section && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.section.classList.add('is-visible');
                this.observer.disconnect();
              }
            });
          },
          { threshold: 0.15 }
        );
        this.observer.observe(this.section);
      } else if (this.section) {
        // Fallback: show immediately
        this.section.classList.add('is-visible');
      }

      // Start auto-rotation if speed > 0 and no reduced motion
      if (this.speed > 0 && !this.reducedMotion.matches) {
        this.play();
      }
    }

    disconnectedCallback() {
      this.pause();
      this.observer?.disconnect();
      this.reducedMotion?.removeEventListener('change', this.handleReducedMotionChange);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.slider?.removeEventListener('slideChanged', this.handleSlideChanged);
      if (Shopify.designMode) {
        this.removeEventListener('shopify:block:select', this.handleBlockSelect);
        this.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }
    }

    play() {
      if (!this.sliderList || this.speed === 0) return;
      this.sliderList.setAttribute('aria-live', 'off');
      clearInterval(this.autoplay);
      this.autoplay = setInterval(() => this.autoRotate(), this.speed);
    }

    pause() {
      if (this.sliderList) this.sliderList.setAttribute('aria-live', 'polite');
      clearInterval(this.autoplay);
      this.autoplay = null;
    }

    autoRotate() {
      if (!this.slider) return;
      const items = this.slider.querySelectorAll('[id^="Slide-"]');
      const sliderEl = this.slider.querySelector('[id^="Slider-"]');
      if (!sliderEl || items.length < 2) return;

      const itemOffset = items[1].offsetLeft - items[0].offsetLeft;
      const currentPage = Math.round(sliderEl.scrollLeft / itemOffset) + 1;
      const nextPosition = currentPage >= items.length ? 0 : sliderEl.scrollLeft + itemOffset;

      sliderEl.scrollTo({ left: nextPosition });
    }

    onSlideChanged() {
      // Pause any playing videos
      this.videos.forEach((video) => {
        if (!video.paused) video.pause();
      });

      // Restart auto-rotation timer (reset interval on manual navigation)
      if (this.autoplay) {
        this.play();
      }
    }

    onBlockSelect(event) {
      // Navigate to the selected block and pause
      this.isPaused = true;
      this.pause();

      const slides = [...this.slider.querySelectorAll('[id^="Slide-"]')];
      const slideIndex = slides.findIndex((slide) => slide.contains(event.target));

      if (slideIndex >= 0 && slides.length > 1) {
        const sliderEl = this.slider.querySelector('[id^="Slider-"]');
        const itemOffset = slides[1].offsetLeft - slides[0].offsetLeft;
        sliderEl.scrollTo({ left: slideIndex * itemOffset });
      }
    }

    onBlockDeselect() {
      this.isPaused = false;
      if (this.speed > 0 && !this.reducedMotion.matches) {
        this.play();
      }
    }

    handleReducedMotionChange() {
      if (this.reducedMotion.matches) {
        this.pause();
      } else if (this.speed > 0 && !this.isPaused) {
        this.play();
      }
    }
  }

  customElements.define('testimonial-carousel', TestimonialCarousel);
}
